import { useState, useEffect, useCallback } from "react";
// Import removed - will use dynamic import for consistency
import type { UpdateStatus } from "@/services/pwaUpdate.service";

interface PWAUpdateHook {
  updateStatus: UpdateStatus;
  checkForUpdates: () => Promise<void>;
  installUpdate: () => Promise<void>;
  isUpdateAvailable: boolean;
  isUpdateReady: boolean;
  isInstalling: boolean;
  lastChecked?: Date;
}

/**
 * React hook for managing PWA updates
 * Provides a clean interface to the PWA update service
 */
export const usePWAUpdate = (): PWAUpdateHook => {
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>({
    isUpdateAvailable: false,
    isUpdateReady: false,
    isInstalling: false,
    registration: undefined,
    lastChecked: undefined,
  });

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | null = null;

    // Initialize PWA update service
    const initService = async () => {
      try {
        const { pwaUpdateService } = await import(
          "@/services/pwaUpdate.service"
        );
        if (!active) return;

        // Get initial status
        setUpdateStatus(pwaUpdateService.getUpdateStatus());

        // Subscribe to update status changes
        unsubscribe = pwaUpdateService.onUpdateStatusChange(
          (status: UpdateStatus) => {
            if (active) setUpdateStatus(status);
          }
        );
      } catch (error) {
        console.error("Error initializing PWA update service:", error);
      }
    };

    initService();

    // Cleanup subscription on unmount
    return () => {
      active = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const checkForUpdates = useCallback(async (): Promise<void> => {
    try {
      const { pwaUpdateService } = await import("@/services/pwaUpdate.service");
      await pwaUpdateService.forceUpdateCheck();
    } catch (error) {
      console.error("Error checking for updates:", error);
    }
  }, []);

  const installUpdate = useCallback(async (): Promise<void> => {
    const { pwaUpdateService } = await import("@/services/pwaUpdate.service");
    await pwaUpdateService.installUpdate();
  }, []);

  return {
    updateStatus,
    checkForUpdates,
    installUpdate,
    isUpdateAvailable: updateStatus.isUpdateAvailable,
    isUpdateReady: updateStatus.isUpdateReady,
    isInstalling: updateStatus.isInstalling,
    lastChecked: updateStatus.lastChecked,
  };
};
